import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiChevronRight, mdiDeleteOutline } from "@mdi/js";
import { useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useMutation, useQueryClient } from "react-query";

import type { ListarAlertasResponse } from "../../model/listarAlertasResponse";
import { deleteAlertasDeletar, getGetAlertasListarQueryKey } from "../../config/swagger";
import { useToast } from "../../context/ToastContext";
import ListCard from "../ListCard";

type AlertasCardPropsContent = {
	data: ListarAlertasResponse;
	skeleton?: never;
};

type AlertasCardPropsSkeleton = {
	data?: never;
	skeleton: true;
};

type AlertasCardProps = AlertasCardPropsContent | AlertasCardPropsSkeleton;

export default function AlertasCard(props: AlertasCardProps) {
	const { handleToast } = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [removeModal, setRemoveModal] = useState(false);
	const [removed, setRemoved] = useState(false);

	const deleteMutation = useMutation((id: number) => deleteAlertasDeletar({ id }), {
		onSuccess: () => {
			queryClient.invalidateQueries(getGetAlertasListarQueryKey());
			setRemoved(true);
			handleToast("Alerta", "Alerta removido com sucesso!", 5000);
			setRemoveModal(false);
		},
		onError: () => {
			handleToast("Alerta", "Erro ao remover alerta", 5000, "danger");
			setRemoveModal(false);
		},
	});

	function handleClick() {
		if (!props.skeleton && props.data) {
			navigate("/alertaFormulario/" + props.data.id);
		}
	}

	function handleClickRemove(e: React.MouseEvent) {
		e.stopPropagation();
		setRemoveModal(true);
	}

	async function handleRemove() {
		if (!props.skeleton && props.data?.id) {
			deleteMutation.mutate(props.data.id);
		}
	}

	function handleCloseModal() {
		if (!deleteMutation.isLoading) {
			setRemoveModal(false);
		}
	}

	if (removed) {
		return <></>;
	}

	let skeletonButton = "";
	if (props.skeleton) {
		skeletonButton = "skeleton skeleton-button";
	}

	return (
		<>
			<ListCard onClick={handleClick}>
				<ListCard.Content>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Nome</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.nome ?? "..."}</ListCard.Text>
					</ListCard.Item>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Descrição</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.descricao ?? "..."}</ListCard.Text>
					</ListCard.Item>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Grupos</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.quantidadeGrupos ?? "..."}</ListCard.Text>
					</ListCard.Item>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Destinatários</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.quantidadeDestinatarios ?? "..."}</ListCard.Text>
					</ListCard.Item>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Data Criação</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.dataCriacao ?? "..."}</ListCard.Text>
					</ListCard.Item>
				</ListCard.Content>
				<ListCard.Action>
					<Button variant="light" className={`me-2 ${skeletonButton}`} onClick={handleClickRemove}>
						<Icon path={mdiDeleteOutline} size={1} className="text-danger" />
					</Button>
					<Icon path={mdiChevronRight} size={1} className={`text-muted m-2 ${skeletonButton}`} />
				</ListCard.Action>
			</ListCard>

			<Modal show={removeModal} onHide={handleCloseModal} centered>
				<Modal.Header closeButton>
					<Modal.Title>Remover Alerta</Modal.Title>
				</Modal.Header>
				<Modal.Body className="text-center">{!deleteMutation.isLoading ? "Deseja realmente remover este alerta?" : <Spinner />}</Modal.Body>
				{!deleteMutation.isLoading && (
					<Modal.Footer>
						<Button variant="secondary" onClick={handleCloseModal}>
							Cancelar
						</Button>
						<Button variant="danger" onClick={handleRemove}>
							Remover
						</Button>
					</Modal.Footer>
				)}
			</Modal>
		</>
	);
}
